import { Box, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { IssueListButton } from "components/Forms/IssueListButton";
import { PrefillWasteFieldsButton } from "components/Forms/PrefillWasteFieldsButton";
import { Issue, ProgramArea, SubmissionStepType } from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacility";
import { WA_EXTRA_STATE_FIELD_KEYS, WA_STATE_CODE } from "hooks/useSubmissionSteps";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useFacilityInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useFacilityInputValidation";
import { FacilityDetailsInput } from "routes/Customer/Facility/FacilityProfile";
import { FacilityStateFieldsForm } from "routes/Customer/Facility/FacilityStateFieldsForm";
import {
  useFacility,
  useStateInformationIssues,
  useUpdateFacilityProfile,
} from "routes/Customer/Facility/useFacility";
import { hasCriticalIssues } from "util/forms";

import { StepHeader } from "../StepHeader";
import { StepSaveFooter } from "./StepSaveFooter";

/**
 * State Information stepper step — an inline editable copy of the facility
 * profile's state-fields form, scoped to the RCRA (Waste) program area
 * (intentionally NOT the shared `EditStateInformationDialog`, so the stepper
 * owns its own on-page layout and the merged "Save & continue" action). Saving
 * persists the waste state fields, marks the `StateFields` step complete, and
 * advances; the AL "Pre-fill from waste data" button sits in the footer.
 *
 * Frozen submissions never reach this editor (they route to the read-only
 * snapshot panel); read-only customers (`!canEdit`) see the form display-only
 * with no save action. The backend only includes this step for facilities whose
 * jurisdiction has RCRA state fields, so there are always waste fields to show.
 */
export function StateFieldsStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { canEdit } = useWasteStepper();
  const isDisplayOnly = !canEdit;

  const { data } = useFacility(facilityId);
  const facility = data?.facility;

  const [updateFacility, { loading: saving }] = useUpdateFacilityProfile();

  const allStateIssues = useStateInformationIssues(facilityId);

  // WA facilities also surface the EPCRA UBI field here (it's required on the
  // 8700-12); everywhere else the step is strictly RCRA. See
  // WA_EXTRA_STATE_FIELD_KEYS.
  const isWa = (facility?.state ?? "") === WA_STATE_CODE;
  const isStepField = useCallback(
    (field: { type?: ProgramArea | null; key: string }) =>
      field.type === ProgramArea.Rcra || (isWa && WA_EXTRA_STATE_FIELD_KEYS.includes(field.key)),
    [isWa]
  );

  // Scope everything (default values, validation, persisted issues) to the RCRA
  // (Waste) state fields, plus the WA extras above.
  const wasteFieldKeys = useMemo(
    () => new Set((facility?.stateFields ?? []).filter(isStepField).map((field) => field.key)),
    [facility?.stateFields, isStepField]
  );
  const persistedIssues = useMemo(
    () => allStateIssues.filter((issue) => !!issue.key && wasteFieldKeys.has(issue.key)),
    [allStateIssues, wasteFieldKeys]
  );

  const defaultValues = useMemo<FacilityDetailsInput>(
    () => ({
      name: facility?.name ?? "",
      state: facility?.state ?? null,
      streetAddress1: facility?.streetAddress1 ?? null,
      streetAddress2: facility?.streetAddress2 ?? null,
      city: facility?.city ?? null,
      zip: facility?.zip ?? null,
      country: facility?.country ?? null,
      county: facility?.county ?? null,
      latitude: facility?.latitude ?? null,
      longitude: facility?.longitude ?? null,
      phone: facility?.phone ?? null,
      phone24Hour: facility?.phone24Hour ?? null,
      stateFields: (facility?.stateFields ?? [])
        .filter(isStepField)
        .map((field) => ({ ...field, value: field.value ?? null })),
    }),
    [facility, isStepField]
  );

  const validate = useFacilityInputValidation({
    facilityId: facility?.id ?? "",
    reportId: undefined,
    pick: ["stateFields"],
  });
  const validator = useCallback(
    async (input: FacilityDetailsInput): Promise<Issue[]> => {
      const result = await validate(input);
      return result.filter((issue) => !!issue.key && wasteFieldKeys.has(issue.key));
    },
    [validate, wasteFieldKeys]
  );

  const form = useValidatingForm<FacilityDetailsInput>(
    defaultValues,
    persistedIssues,
    validator,
    undefined,
    { isDisplayOnly }
  );
  const { handleSubmit, issues, watch } = form;
  const stateFields = watch("stateFields");

  const jurisdictions = useMemo(
    () =>
      [facility?.state, facility?.lepc?.id, facility?.fireDepartment?.id].filter(
        Boolean
      ) as string[],
    [facility?.state, facility?.lepc?.id, facility?.fireDepartment?.id]
  );

  // Persist only — StepSaveFooter owns the step-status touch, toast, and
  // navigation. Resolve `false` when validation blocks the save (or there's no
  // facility yet) so the footer leaves the step status untouched.
  const persist = useCallback(
    async (formData: FacilityDetailsInput): Promise<boolean> => {
      if (!facility) return false;
      const input = transform(formData);
      try {
        await updateFacility({
          variables: { id: facility.id, facility: { stateFields: input.stateFields } },
        });
        return true;
      } catch {
        alerts.error("Error saving state information.");
        return false;
      }
    },
    [facility, updateFacility, alerts]
  );

  const onSave = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        void handleSubmit(
          async (formData) => resolve(await persist(formData)),
          () => resolve(false)
        )();
      }),
    [handleSubmit, persist]
  );

  return (
    <Stack spacing={2}>
      <StepHeader title="State Information" />
      <Typography variant="body2" color="text.secondary">
        Some states require additional information to be included on the submission. Confirm or
        update the information for your facility. Many custom state fields are optional or can be
        calculated based on other data you have already entered, so Encamp does not display them
        here.
      </Typography>
      <FormProvider {...form}>
        <form>
          {/* Constrain the inputs so they don't stretch the full content width. */}
          <Box sx={{ maxWidth: 560 }}>
            <FacilityStateFieldsForm
              jurisdictions={jurisdictions}
              context="facility"
              fields={stateFields ?? []}
              namePrefix="stateFields"
              programArea={ProgramArea.Rcra}
              additionalKeys={isWa ? WA_EXTRA_STATE_FIELD_KEYS : undefined}
              readOnly={isDisplayOnly}
              flush
            />
          </Box>
          {canEdit && (
            <StepSaveFooter
              submissionId={submissionId}
              step={SubmissionStepType.StateFields}
              onSave={onSave}
              saving={saving}
              disabled={hasCriticalIssues(issues)}
              leftActions={
                <>
                  <PrefillWasteFieldsButton
                    fields={stateFields ?? []}
                    namePrefix="stateFields"
                    facilityId={facility?.id}
                  />
                  <IssueListButton issues={issues} />
                </>
              }
            />
          )}
        </form>
      </FormProvider>
    </Stack>
  );
}
